/**
 * Node EAD report language profiles (EN / ZH / JA / KO only).
 */

import { getLocale, type UiLocale } from "./i18n.ts";

export const NODE_REPORT_LANGUAGES = ["en", "zh", "ja", "ko"] as const;
export type NodeReportLanguage = (typeof NODE_REPORT_LANGUAGES)[number];

export type NodeReportLanguageProfile = {
  code: NodeReportLanguage;
  name: string;
  nodeLabel: string;
  headings: {
    nodeDescription: string;
    mainFeatures: string;
    feature: string;
    testCase: string;
    procedureSteps: string;
  };
  fields: {
    objective: string;
    preconditions: string;
    testData: string;
    expectedResult: string;
    evidence: string;
    steps: string;
  };
};

const PROFILES: Record<NodeReportLanguage, NodeReportLanguageProfile> = {
  en: {
    code: "en",
    name: "English",
    nodeLabel: "Node",
    headings: {
      nodeDescription: "Node Description",
      mainFeatures: "Main Features",
      feature: "Feature",
      testCase: "Test Case",
      procedureSteps: "Procedure Steps",
    },
    fields: {
      objective: "Objective",
      preconditions: "Preconditions",
      testData: "Test Data",
      expectedResult: "Expected Result",
      evidence: "Evidence",
      steps: "Steps",
    },
  },
  zh: {
    code: "zh",
    name: "Chinese (Simplified)",
    nodeLabel: "节点",
    headings: {
      nodeDescription: "节点描述",
      mainFeatures: "主要功能",
      feature: "功能",
      testCase: "测试用例",
      procedureSteps: "操作步骤",
    },
    fields: {
      objective: "目标",
      preconditions: "前置条件",
      testData: "测试数据",
      expectedResult: "预期结果",
      evidence: "证据",
      steps: "步骤",
    },
  },
  ja: {
    code: "ja",
    name: "Japanese",
    nodeLabel: "ノード",
    headings: {
      nodeDescription: "ノード説明",
      mainFeatures: "主要機能",
      feature: "機能",
      testCase: "テストケース",
      procedureSteps: "手順",
    },
    fields: {
      objective: "目的",
      preconditions: "前提条件",
      testData: "テストデータ",
      expectedResult: "期待結果",
      evidence: "証拠",
      steps: "ステップ",
    },
  },
  ko: {
    code: "ko",
    name: "Korean",
    nodeLabel: "노드",
    headings: {
      nodeDescription: "노드 설명",
      mainFeatures: "주요 기능",
      feature: "기능",
      testCase: "테스트 케이스",
      procedureSteps: "절차 단계",
    },
    fields: {
      objective: "목표",
      preconditions: "사전 조건",
      testData: "테스트 데이터",
      expectedResult: "예상 결과",
      evidence: "증거",
      steps: "단계",
    },
  },
};

export function nodeReportLanguageProfile(code: NodeReportLanguage): NodeReportLanguageProfile {
  return PROFILES[code] || PROFILES.en;
}

/** Map UI locale / run AI language to one of the four node-report languages. */
export function resolveNodeReportLanguage(options?: {
  aiAnswerLanguage?: string;
  locale?: UiLocale;
}): NodeReportLanguage {
  const loc = options?.locale ?? getLocale();
  const raw = String(options?.aiAnswerLanguage || "").trim().toLowerCase();
  if (raw === "zh" || raw === "zh-tw" || raw === "zh-cn") return "zh";
  if (raw === "ja") return "ja";
  if (raw === "ko") return "ko";
  if (raw === "en") return "en";
  if (loc === "zh" || loc === "ja" || loc === "ko") return loc;
  return "en";
}

let _matchersCache: ReturnType<typeof buildMatchers> | null = null;

function buildMatchers() {
  const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const all = Object.values(PROFILES);
  const join = (pick: (p: NodeReportLanguageProfile) => string) =>
    all.map((p) => escape(pick(p))).join("|");
  const nodeLabels = all.map((p) => escape(p.nodeLabel)).join("|");
  return {
    nodeHeader: new RegExp(`^#{1,3}\\s+(${nodeLabels}|node)\\s+`, "im"),
    nodeDescription: new RegExp(`(${join((p) => p.headings.nodeDescription)}|node\\s+description)`, "i"),
    mainFeatures: new RegExp(
      `(${join((p) => p.headings.mainFeatures)}|main\\s+features|\\bfeatures\\b)`,
      "i",
    ),
    testCase: new RegExp(
      `(${join((p) => p.headings.testCase)}|test\\s*case|procedure\\s+steps)`,
      "i",
    ),
    feature: new RegExp(
      `(${join((p) => p.headings.feature)}|feature)\\s+[a-z0-9._-]+|feature\\s+f-\\d+`,
      "i",
    ),
    step: new RegExp(`\\b(${join((p) => p.fields.steps)}|step)\\s+\\d+\\s*:`, "i"),
    procedureSteps: /procedure\s+steps/i,
  };
}

export function nodeReportLanguageMatchers() {
  if (!_matchersCache) _matchersCache = buildMatchers();
  return _matchersCache;
}
